/**
 * Workflow-run submit data-source function.
 *
 * Sends a client-generated `Idempotency-Key` for forward-compatibility.
 * NOTE: workflow-service does NOT yet honour this header server-side (dedup is
 * not implemented on the BE). The real guard against double-submit today is the
 * pending-state button lock in the UI.
 *
 * Human submits enroll a delegated session first and send
 * `x-workflow-session-binding` so Record Service field-matches the user.
 */
import type { WorkflowRunSubmitResponseData } from "~/api/generated/workflow-service/model";
import { submitWorkflowRun as submitWorkflowRunRequest } from "~/api/generated/workflow-service/workflow-runs/workflow-runs";
import { getRequestContext } from "~/api/requestContextStore";
import { safeSameOriginPath } from "~/lib/safeUrl";

import {
  type PendingCreateResume,
  resolveSessionBindingForSubmit,
} from "../lib/workflowSessionEnrollment";
import { readEnvelope } from "./envelope";

/**
 * Envelope the live workflow-service requires on `POST …/runs`.
 *
 * Older builds accepted the business fields at the top level. Current OpenAPI
 * (`WorkflowRunSubmissionRequest`) requires `{ input, context }` with
 * `context.trigger.type`, and rejects anything else as 400
 * "provided object should contain property input".
 */
export type WorkflowRunTriggerType =
  | "RECORD_ACTION"
  | "LIFECYCLE_TRANSITION"
  | "REACTION"
  | "DIRECT";

export interface WorkflowRunSubjectRef {
  resource: string;
  resourceVersion: string;
  pk: string;
}

export interface WorkflowRunContext {
  correlationId?: string;
  subjectRef?: WorkflowRunSubjectRef;
  trigger: {
    type: WorkflowRunTriggerType;
    actionKey?: string;
    transitionKey?: string;
    lifecycleCommandId?: string;
  };
}

const DEFAULT_RUN_CONTEXT: WorkflowRunContext = { trigger: { type: "DIRECT" } };

export const submitWorkflowRun = async (
  definitionKey: string,
  input: Record<string, unknown>,
  idempotencyKey: string,
  definitionVersion?: string,
  context: WorkflowRunContext = DEFAULT_RUN_CONTEXT,
  sessionBindingHandle?: string,
  createResume?: PendingCreateResume,
): Promise<WorkflowRunSubmitResponseData> => {
  const binding =
    sessionBindingHandle?.trim() ||
    (await resolveSessionBindingForSubmit({
      definitionKey,
      input,
      idempotencyKey,
      definitionVersion,
      context,
      tenantId: getRequestContext()?.tenantId ?? undefined,
      returnPath:
        typeof window !== "undefined"
          ? safeSameOriginPath(`${window.location.pathname}${window.location.search}`)
          : "/",
      createResume,
    }));

  // OpenAPI requires `{ input, context }`. UI-initiated runs default to DIRECT;
  // RECORD_ACTION / lifecycle callers can pass a richer `context` without
  // changing this wrapper's positional signature.
  const response = await submitWorkflowRunRequest(
    definitionKey,
    { input, context },
    {
      headers: {
        "Idempotency-Key": idempotencyKey,
        ...(definitionVersion ? { "X-Workflow-Definition-Version": definitionVersion } : {}),
        ...(binding ? { "x-workflow-session-binding": binding } : {}),
      },
    },
  );

  return readEnvelope<WorkflowRunSubmitResponseData>(response);
};
